import React, { useState } from "react";
import {
  Box,
  Typography,
  Grid,
  Button,
  Stack,
  IconButton,
  Tooltip,
  Chip,
  alpha,
  useTheme,
  Paper,
  TextField,
  Avatar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Divider,
  Link,
  MenuItem,
  useMediaQuery
} from "@mui/material";
import {
  Search as SearchIcon,
  Sync as SyncIcon,
  PlayArrow as PlayIcon,
  Movie as MovieIcon,
  Star as StarIcon,
  Language as LanguageIcon,
  Category as GenreIcon,
  History as DurationIcon,
  Person as DirectorIcon,
  Launch as LaunchIcon,
  Radar as ProgressIcon,
  Delete as TrashIcon
} from "@mui/icons-material";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "react-toastify";

import moment from "moment";

const TMDBPreview = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [movies, setMovies] = useState([]);
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [region, setRegion] = useState("IN");
  const [page, setPage] = useState(1);
  const [language, setLanguage] = useState("");
  const [genre, setGenre] = useState("");

  const fetchTMDBMovies = async () => {
    setLoading(true);
    try {
      const API_URL = process.env.REACT_APP_API_URL || "http://localhost:5000/api";
      const params = new URLSearchParams({
        region,
        page: page.toString(),
        ...(searchTerm && { search: searchTerm }),
        ...(language && { language }),
        ...(genre && { genre })
      });

      const response = await fetch(`${API_URL}/sync/fetch-tmdb-movies?${params.toString()}`);
      const data = await response.json();

      if (data.movies) {
        setMovies(data.movies);
        toast.success(`Telemetry retrieved for ${data.total || data.movies.length} profiles`);
      } else {
        setMovies([]);
        toast.info("No matching profiles found in global registry");
      }
    } catch (error) {
      console.error("Error fetching TMDB movies:", error);
      toast.error("Global intelligence sync failure");
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveMovie = (movieId) => {
    setMovies(prev => prev.filter(m => m.id !== movieId));
    toast.info("Profile removed from temporary registry");
  };

  const syncMovies = async () => {
    if (movies.length === 0) {
      toast.warning("Registry empty - nothing to synchronize");
      return;
    }

    setSyncing(true);
    try {
      const API_URL = process.env.REACT_APP_API_URL || "http://localhost:5000/api";
      // Using the more advanced add-movies endpoint if available, otherwise fallback
      const response = await fetch(`${API_URL}/sync/sync-tmdb-movies`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ 
          movies: movies, // Send the specifically filtered list
          region, 
          page,
          ...(searchTerm && { search: searchTerm }),
          ...(language && { language }),
          ...(genre && { genre })
         })
      });
      const data = await response.json();

      toast.success(`Success: ${data.added || 0} established, ${data.updated || 0} optimized`);
      setMovies([]);
      setSearchTerm("");
    } catch (error) {
      console.error("Error syncing movies:", error);
      toast.error("Database commit failure");
    } finally {
      setSyncing(false);
    }
  };

  return (
    <Box sx={{ width: "100%", pb: 4 }}>
        <motion.div initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }}>
          {/* Core Page Header */}
          <Stack
            direction={{ xs: "column", md: "row" }}
            justifyContent="space-between"
            alignItems={{ xs: "flex-start", md: "center" }}
            mb={{ xs: 3, md: 5 }}
            gap={{ xs: 2.5, md: 3 }}
          >
            <Box>
              <Typography variant="h3" sx={{ fontWeight: 950, letterSpacing: "-0.04em", display: "flex", alignItems: "center", gap: 2.5, fontSize: { xs: "1.75rem", sm: "2.25rem", md: "3rem" } }}>
                <Box sx={{ p: 1, borderRadius: "12px", bgcolor: alpha(theme.palette.primary.main, 0.1), color: theme.palette.primary.main, display: "flex", border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`, flexShrink: 0 }}>
                  <SyncIcon sx={{ fontSize: { xs: 22, md: 28 } }} />
                </Box>
                Global Intelligence
              </Typography>
              <Typography variant="body2" sx={{ color: "text.secondary", fontWeight: 800, mt: 1, display: "flex", alignItems: "center", gap: 1 }}>
                <SearchIcon sx={{ fontSize: 16, color: "secondary.main" }} /> TMDB REAL-TIME METADATA SEARCH & CATALOG SYNCHRONIZATION
              </Typography>
            </Box>
            <Button
              variant="contained"
              startIcon={syncing ? <CircularProgress size={18} color="inherit" /> : <SyncIcon />}
              onClick={syncMovies}
              disabled={syncing || movies.length === 0}
              fullWidth={isMobile}
              sx={{
                py: 1.8,
                px: 4.5,
                borderRadius: "16px",
                fontWeight: 900,
                background: theme.palette.primary.main,
                boxShadow: `0 10px 25px -5px ${alpha(theme.palette.primary.main, 0.3)}`,
                textTransform: "none",
                "&:hover": { background: alpha(theme.palette.primary.main, 0.9), transform: "translateY(-2px)" },
                transition: "all 0.2s"
              }}
            >
              {syncing ? "Optimizing Assets..." : `Sync ${movies.length} Profiles`}
            </Button>
          </Stack>

          {/* Premium Search Command Bar */}
          <Paper elevation={0} sx={{ p: 3, mb: 6, borderRadius: "32px", bgcolor: alpha(theme.palette.background.default, 0.5), border: `1px solid ${theme.palette.divider}`, boxShadow: "0 20px 40px rgba(0,0,0,0.02)" }}>
            <Grid container spacing={2.5} alignItems="center">
              {/* Search Query */}
              <Grid item xs={12} lg={4}>
                <Box sx={{ position: "relative" }}>
                  <SearchIcon sx={{ position: "absolute", left: 16, top: "50%", transform: "translateY(-50%)", color: "text.secondary", opacity: 0.5, fontSize: 20 }} />
                  <TextField
                    fullWidth
                    placeholder="Query global production registry..."
                    variant="standard"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    onKeyPress={(e) => e.key === "Enter" && fetchTMDBMovies()}
                    InputProps={{ 
                      disableUnderline: true, 
                      sx: { 
                        pl: 6, pr: 2, height: 50, fontWeight: 700, fontSize: "0.9rem",
                        bgcolor: alpha(theme.palette.text.primary, 0.03), borderRadius: "14px",
                        border: `1px solid ${alpha(theme.palette.divider, 0.5)}`
                      } 
                    }}
                  />
                </Box>
              </Grid>

              {/* Region Filter */}
              <Grid item xs={12} sm={6} md={3} lg={1.5}>
                <TextField
                  select
                  fullWidth
                  label="Region"
                  value={region}
                  onChange={(e) => setRegion(e.target.value)}
                  variant="outlined"
                  size="small"
                  sx={{ "& .MuiOutlinedInput-root": { borderRadius: "14px", fontWeight: 700 } }}
                >
                  <MenuItem value="IN">🇮🇳 IN</MenuItem>
                  <MenuItem value="US">🇺🇸 US</MenuItem>
                  <MenuItem value="GB">🇬🇧 GB</MenuItem>
                  <MenuItem value="CA">🇨🇦 CA</MenuItem>
                  <MenuItem value="AU">🇦🇺 AU</MenuItem>
                </TextField>
              </Grid>

              {/* Language Filter */}
              <Grid item xs={12} sm={6} md={3} lg={2}>
                <TextField
                  select
                  fullWidth
                  label="Language"
                  value={language}
                  onChange={(e) => setLanguage(e.target.value)}
                  variant="outlined"
                  size="small"
                  sx={{ "& .MuiOutlinedInput-root": { borderRadius: "14px", fontWeight: 700 } }}
                >
                  <MenuItem value="">Global</MenuItem>
                  <MenuItem value="en">English</MenuItem>
                  <MenuItem value="hi">Hindi</MenuItem>
                  <MenuItem value="ta">Tamil</MenuItem>
                  <MenuItem value="te">Telugu</MenuItem>
                  <MenuItem value="ml">Malayalam</MenuItem>
                  <MenuItem value="kn">Kannada</MenuItem>
                </TextField>
              </Grid>

              {/* Genre Filter */}
              <Grid item xs={12} sm={6} md={3} lg={2}>
                <TextField
                  select
                  fullWidth
                  label="Genre"
                  value={genre}
                  onChange={(e) => setGenre(e.target.value)}
                  variant="outlined"
                  size="small"
                  sx={{ "& .MuiOutlinedInput-root": { borderRadius: "14px", fontWeight: 700 } }}
                >
                  <MenuItem value="">All Genres</MenuItem>
                  <MenuItem value="28">Action</MenuItem>
                  <MenuItem value="12">Adventure</MenuItem>
                  <MenuItem value="16">Animation</MenuItem>
                  <MenuItem value="35">Comedy</MenuItem>
                  <MenuItem value="18">Drama</MenuItem>
                  <MenuItem value="27">Horror</MenuItem>
                  <MenuItem value="10749">Romance</MenuItem>
                  <MenuItem value="878">Sci-Fi</MenuItem>
                  <MenuItem value="53">Thriller</MenuItem>
                </TextField>
              </Grid>

              {/* Page Selector */}
              <Grid item xs={12} sm={6} md={3} lg={1}>
                <TextField
                  type="number"
                  fullWidth
                  label="Page"
                  value={page}
                  onChange={(e) => setPage(Math.max(1, parseInt(e.target.value) || 1))}
                  variant="outlined"
                  size="small"
                  inputProps={{ min: 1 }}
                  sx={{ "& .MuiOutlinedInput-root": { borderRadius: "14px", fontWeight: 700 } }}
                />
              </Grid>

              {/* Action Button */}
              <Grid item xs={12} lg={1.5}>
                <Button
                  fullWidth
                  variant="contained"
                  onClick={fetchTMDBMovies}
                  disabled={loading}
                  sx={{
                    height: 50, borderRadius: "14px", fontWeight: 900, textTransform: "none",
                    bgcolor: "secondary.main", boxShadow: `0 8px 16px ${alpha(theme.palette.secondary.main, 0.3)}`,
                    "&:hover": { bgcolor: "secondary.dark", transform: "translateY(-2px)" }
                  }}
                >
                  {loading ? <CircularProgress size={20} color="inherit" /> : "Fetch"}
                </Button>
              </Grid>
            </Grid>
          </Paper>

          {/* Results Registry */}
          {movies.length > 0 ? (
            <TableContainer component={Paper} elevation={0} sx={{ borderRadius: "30px", border: `1px solid ${theme.palette.divider}`, overflowX: "auto", bgcolor: theme.palette.background.paper }}>
              <Table>
                <TableHead>
                  <TableRow sx={{ bgcolor: alpha(theme.palette.background.default, 0.5) }}>
                    <TableCell sx={{ fontWeight: 900, fontSize: "0.7rem", color: "text.secondary", py: 2.5, px: 4 }}>#</TableCell>
                    <TableCell sx={{ fontWeight: 900, fontSize: "0.7rem", color: "text.secondary", py: 2.5 }}>PRODUCTION PROFILE</TableCell>
                    <TableCell sx={{ fontWeight: 900, fontSize: "0.7rem", color: "text.secondary", py: 2.5 }}>TIMELINE</TableCell>
                    <TableCell sx={{ fontWeight: 900, fontSize: "0.7rem", color: "text.secondary", py: 2.5 }}>METRICS</TableCell>
                    <TableCell sx={{ fontWeight: 900, fontSize: "0.7rem", color: "text.secondary", py: 2.5 }}>CLASSIFICATION</TableCell>
                    <TableCell sx={{ fontWeight: 900, fontSize: "0.7rem", color: "text.secondary", py: 2.5 }}>DURATION</TableCell>
                    <TableCell sx={{ fontWeight: 900, fontSize: "0.7rem", color: "text.secondary", py: 2.5, textAlign: "right", px: 4 }}>OPERATIONAL LINKS</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {movies.map((movie, index) => (
                    <TableRow key={movie.id} hover sx={{ "&:last-child td, &:last-child th": { border: 0 } }}>
                      <TableCell sx={{ px: 4, py: 2.5 }}><Typography variant="body2" sx={{ fontWeight: 900, color: "text.secondary", opacity: 0.5 }}>{index + 1}</Typography></TableCell>
                      <TableCell sx={{ py: 2.5 }}>
                        <Stack direction="row" spacing={2} alignItems="center">
                          <Avatar variant="rounded" src={movie.poster} sx={{ width: 44, height: 64, borderRadius: "10px", boxShadow: "0 4px 12px rgba(0,0,0,0.08)" }}>
                            <MovieIcon sx={{ fontSize: 20 }} />
                          </Avatar>
                          <Box>
                            <Typography variant="body1" sx={{ fontWeight: 900, color: theme.palette.text.primary, lineHeight: 1.2 }}>{movie.title}</Typography>
                            <Typography variant="caption" sx={{ fontWeight: 700, color: "text.secondary", textTransform: "uppercase", fontSize: "10px" }}>{movie.director || "Unknown Director"}</Typography>
                          </Box>
                        </Stack>
                      </TableCell>
                      <TableCell>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <DirectorIcon sx={{ fontSize: 16, opacity: 0.3 }} />
                          <Typography variant="body2" sx={{ fontWeight: 700 }}>{movie.releaseDate ? moment(movie.releaseDate).format("MMM DD, YYYY") : "Awaiting"}</Typography>
                        </Stack>
                      </TableCell>
                      <TableCell>
                        <Chip
                          icon={<StarIcon sx={{ fontSize: "14px !important", color: "#ffab00 !important" }} />}
                          label={movie.rating?.toFixed(1) || "0.0"}
                          size="small"
                          sx={{ fontWeight: 950, bgcolor: alpha("#ffab00", 0.08), color: "#ffab00", border: `1px solid ${alpha("#ffab00", 0.15)}` }}
                        />
                      </TableCell>
                      <TableCell>
                        <Stack spacing={0.5} direction="row" flexWrap="wrap">
                          {movie.genres?.slice(0, 2).map((g, i) => (
                            <Chip key={i} label={g} size="small" variant="outlined" sx={{ fontWeight: 800, fontSize: "0.6rem", height: 20, borderColor: "divider" }} />
                          ))}
                          <Chip label={movie.language?.toUpperCase() || "INTL"} size="small" sx={{ fontWeight: 900, height: 20, bgcolor: "primary.light", color: "primary.main", fontSize: "0.55rem" }} />
                        </Stack>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 800, color: "text.secondary" }}>{movie.duration || "---"} <Typography variant="caption" component="span" sx={{ fontWeight: 700, opacity: 0.5 }}>MINS</Typography></Typography>
                      </TableCell>
                      <TableCell sx={{ textAlign: "right", px: 4 }}>
                        <Stack direction="row" spacing={1} justifyContent="flex-end">
                          {movie.youtubeUrl ? (
                            <Tooltip title="Examine Intel - Open Source Stream">
                              <IconButton
                                size="small"
                                component="a"
                                href={movie.youtubeUrl}
                                target="_blank"
                                sx={{ 
                                  color: "primary.main", bgcolor: alpha(theme.palette.primary.main, 0.05),
                                  borderRadius: "10px", "&:hover": { bgcolor: alpha(theme.palette.primary.main, 0.1) }
                                }}
                              >
                                <PlayIcon sx={{ fontSize: 18 }} />
                              </IconButton>
                            </Tooltip>
                          ) : (
                            <Typography variant="caption" sx={{ fontWeight: 900, opacity: 0.3, alignSelf: "center" }}>NO SOURCE</Typography>
                          )}
                          <Tooltip title="Prune Profile - Remove from Registry">
                            <IconButton
                              size="small"
                              onClick={() => handleRemoveMovie(movie.id)}
                              sx={{ 
                                color: "error.main", bgcolor: alpha(theme.palette.error.main, 0.05),
                                borderRadius: "10px", "&:hover": { bgcolor: alpha(theme.palette.error.main, 0.1) }
                              }}
                            >
                              <TrashIcon sx={{ fontSize: 18 }} />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Box sx={{ py: 15, textAlign: "center", opacity: 0.5 }}>
              <ProgressIcon sx={{ fontSize: 64, mb: 3 }} />
              <Typography variant="h5" sx={{ fontWeight: 850 }}>{searchTerm ? "No assets found in global registry" : "Access global cinematic telemetry by querying title"}</Typography>
              <Typography variant="body2" sx={{ fontWeight: 700, mt: 1 }}>{searchTerm ? "Search query positive, result array negative" : "Input production keywords to initiate profile retrieval"}</Typography>
            </Box>
          )}
        </motion.div>
    </Box>
  );
};

export default TMDBPreview;

